import React, { useMemo } from 'react';

import { BackendOrganizationRecord } from 'utils/employment/types';
import { transformEmploymentData } from 'components/Artist/Edit/ProfileInfo/EmploymentHistorySection/hooks/useEmploymentData';
import { transformToGroupedTimeline } from 'utils/employment/transformers';

import { useTranslation } from 'src/i18n';

import ReadOnlyOrganizationGroup from './ReadOnlyOrganizationGroup';
import ReadOnlyEmploymentTimeline from './ReadOnlyEmploymentTimeline';

import styles from './BiographyEmploymentTimeline.module.scss';

export interface BiographyEmploymentTimelineProps {
  /** Organizations data from entity */
  organizations: BackendOrganizationRecord[];
  /** Entity type for context */
  entityType: string;
  /** Custom className for styling */
  className?: string;
  /** Test ID for testing */
  testId?: string;
}

const BiographyEmploymentTimeline: React.FC<BiographyEmploymentTimelineProps> = ({
  organizations,
  entityType,
  className,
  testId = 'biography-employment-timeline',
}) => {
  const { t } = useTranslation('NS_EDIT');
  // Transform organizations data to employment records
  const employmentList = useMemo(() => {
    try {
      const allRecords = transformEmploymentData(organizations);
      // Filter to only show visible records (displayStatus: 2) in public biography
      return allRecords.filter(record => record.displayStatus === 2);
    } catch (error) {
      console.error('Error transforming employment data for biography:', error);
      return [];
    }
  }, [organizations]);

  // Transform employment list to grouped timeline data
  const groupedTimelineData = useMemo(() => {
    try {
      return transformToGroupedTimeline(employmentList);
    } catch (error) {
      console.error('Error creating grouped timeline data for biography:', error);
      return {
        groups: [],
        totalRecords: 0,
        totalOrganizations: 0,
        isEmpty: true,
      };
    }
  }, [employmentList]);

  // Don't render if no employment data
  if (groupedTimelineData.isEmpty || groupedTimelineData.groups.length === 0) {
    return null;
  }

  return (
    <div 
      className={className}
      {...(testId && { 'data-testid': testId })}
    >
      <h3 className={styles.sectionTitle}>{t(`operabase.general.FN_EMPLOYMENT_HISTORY`)}</h3>
      <div className={styles.timelineContainer}>
        {groupedTimelineData.groups.map((group, groupIndex) => (
          <ReadOnlyOrganizationGroup
            key={`${group.organization.id}-${group.period.start}`}
            group={group}
            isFirst={groupIndex === 0}
            isLast={groupIndex === groupedTimelineData.groups.length - 1}
            testId={`${testId}-organization-group-${group.organization.id}`}
          >
            <ReadOnlyEmploymentTimeline
              employmentRecords={group.employmentRecords}
              testId={`${testId}-timeline-${group.organization.id}`}
            />
          </ReadOnlyOrganizationGroup>
        ))}
      </div>
    </div>
  );
};

export default BiographyEmploymentTimeline; 